import { OralExamConductPhase } from '@/common/api/models/oralExamConductPhase';
import { OralExamDriver } from '@/common/api/models/oralExamDriver';
import { OralExamOperationalFault } from '@/common/api/models/oralExamOperationalFault';
import { OralExamSessionPhase } from '@/common/api/models/oralExamSessionPhase';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import { resolveFaultPresentation } from '@/common/oralExam/faultPresentation';
import { isOneToOneExamSurface } from '@/common/utils/oralExam/runConduct';
import {
  evaluateStageGate,
  OralExamStageGateCode,
  type StageGateCohortSnapshot,
  type StageGateRunSnapshot,
} from '@/common/oralExam/stageGates';
import { deriveStudentExamShellPhase } from '@/student/module/utils/deriveStudentRuntimeJourney';

/** Booking-backed journey inputs for CTA enablement (ADR-002 poll projection). */
export type OralExamJourneySnapshot = {
  role: 'student' | 'lecturer';
  sessionPhase?: OralExamSessionPhase | null;
  runState: string;
  conductPhase?: OralExamConductPhase | null;
  isPaused?: boolean;
  pauseReason?: string | null;
  studentDoneAt?: string | null;
  currentQuestion?: OralExamRunStateResponse['currentQuestion'];
  currentQuestionIndex?: number | null;
  operationalFault?: OralExamOperationalFault | null;
  driver?: OralExamDriver;
  /** Answer-window phase once conduct is in_exam with a presented question. */
  questionPhase?: 'reading' | 'answering';
  isMarkingStudentReady?: boolean;
  isMutationPending?: boolean;
  hasIntegrityBreach?: boolean;
  /** True once the examiner formally began — reset is pre-commit only. */
  committed?: boolean;
  /** Stage-entry diagnosis from poll; false blocks reset/recovery CTAs. */
  stageGateOk?: boolean;
  /** Capacity=1 lecturer-driven mode toggles conducting chrome. */
  mode?: OralExamRunStateResponse['mode'];
  capacity?: number | null;
};

export type StudentCtaGate = {
  startExam: boolean;
  done: boolean;
  requestHelp: boolean;
  abort: boolean;
};

export type LecturerCtaGate = {
  dropIn: boolean;
  next: boolean;
  takeOver: boolean;
  advanceQuestion: boolean;
  endExam: boolean;
  resetExam: boolean;
  /** Re-stamp answering window on current question (1:1 conducting). */
  resetTimer: boolean;
  /** Re-open the previous selected question (1:N drop-in). */
  repeatPrevious: boolean;
  /** Cancel booking + allow re-book (left-behind uncommitted in in_exam cohort). */
  cancelBooking: boolean;
  abortExam: boolean;
  /** Begin Q1 after conference hold (1:1 in_exam, no marker yet). */
  startExam: boolean;
};

/** Per-tile cohort console inputs for reset vs cancel recovery CTAs. */
export type LecturerCohortTileGateInput = {
  bookingId: string;
  runState: string;
  conductPhase?: OralExamConductPhase | null;
  committed?: boolean;
  stageGateOk?: boolean;
  sessionPhase?: OralExamSessionPhase | null;
  inExamEnteredAt?: string | null;
  isAdmitted?: boolean;
  isMutationPending?: boolean;
};

function isActiveInExamSession(snapshot: OralExamJourneySnapshot): boolean {
  return snapshot.sessionPhase === OralExamSessionPhase.in_exam
    && snapshot.runState === 'in_progress';
}

function shouldRemoveInsteadOfDropIn(snapshot: OralExamJourneySnapshot): boolean {
  const fault = snapshot.operationalFault;
  if (!fault) return false;
  return resolveFaultPresentation(fault, { capacity: snapshot.capacity }).recoveryCta === 'leave';
}

function shouldOfferTakeOver(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  snapshot: OralExamJourneySnapshot,
): boolean {
  return false;
}

function isStudentStartDisabled(snapshot: OralExamJourneySnapshot): boolean {
  const shellPhase = deriveStudentExamShellPhase(
    snapshot.conductPhase ?? undefined,
    snapshot.currentQuestion ?? null,
    snapshot.currentQuestionIndex,
  );
  if (shellPhase === 'waiting_for_examiner') return true;
  if (shellPhase === 'starting' || snapshot.isMarkingStudentReady) return true;
  return false;
}

function isStudentDoneDisabled(snapshot: OralExamJourneySnapshot): boolean {
  // Done is allowed once the card is shown — do not lock on reading phase.
  if (snapshot.studentDoneAt || snapshot.isPaused) return true;
  return false;
}

function isLeftBehindUncommittedInExam(input: LecturerCohortTileGateInput): boolean {
  if (input.committed === true) return false;
  if (input.sessionPhase !== OralExamSessionPhase.in_exam) return false;
  if (!input.inExamEnteredAt || input.isAdmitted !== true) return false;

  const cohort: StageGateCohortSnapshot = {
    sessionPhase: input.sessionPhase,
    inExamEnteredAt: input.inExamEnteredAt,
    admittedBookingIds: [input.bookingId],
  };
  const run: StageGateRunSnapshot = {
    state: input.runState,
    sessionPhase: input.sessionPhase,
    conductPhase: input.conductPhase,
    committed: input.committed,
  };
  const gate = evaluateStageGate(run, cohort, input.bookingId);
  return !gate.ok && gate.code === OralExamStageGateCode.inconsistentInExamPreStart;
}

/** Cohort supervision tile: reset pre-commit wipe vs cancel left-behind booking. */
export function resolveLecturerCohortTileGate(
  input: LecturerCohortTileGateInput,
): Pick<LecturerCtaGate, 'resetExam' | 'cancelBooking'> {
  const pending = input.isMutationPending ?? false;
  const leftBehind = isLeftBehindUncommittedInExam(input);
  const inProgress = input.runState === 'in_progress';
  const resetAllowed = inProgress
    && !pending
    && input.committed !== true
    && input.stageGateOk !== false
    && !leftBehind;

  return {
    resetExam: resetAllowed,
    cancelBooking: leftBehind && !pending,
  };
}

/** Student exam-room CTAs from booking-backed conductPhase / sessionPhase / pause. */
export function resolveStudentCtaGate(snapshot: OralExamJourneySnapshot): StudentCtaGate {
  const inExamSession = isActiveInExamSession(snapshot);
  const shellPhase = deriveStudentExamShellPhase(
    snapshot.conductPhase ?? undefined,
    snapshot.currentQuestion ?? null,
    snapshot.currentQuestionIndex,
  );
  const inQuestionFlow = shellPhase === 'question_active' || shellPhase === 'between_questions';

  return {
    startExam: inExamSession
      && shellPhase === 'ready_to_start'
      && !isStudentStartDisabled(snapshot),
    done: inExamSession
      && shellPhase === 'question_active'
      && !isStudentDoneDisabled(snapshot),
    requestHelp: inExamSession && inQuestionFlow && !snapshot.isPaused,
    abort: inExamSession,
  };
}

/** Lecturer supervision / conducting CTAs from booking-backed run state. */
export function resolveLecturerCtaGate(snapshot: OralExamJourneySnapshot): LecturerCtaGate {
  const inProgress = snapshot.runState === 'in_progress';
  const inExamConduct = snapshot.conductPhase === OralExamConductPhase.in_exam;
  const pending = snapshot.isMutationPending ?? false;
  const needsNext = Boolean(snapshot.studentDoneAt);
  const isOneToOne = isOneToOneExamSurface(snapshot);
  const hasQuestion = Boolean(snapshot.currentQuestion?.question?.trim());
  const resetAllowed = inProgress
    && !pending
    && snapshot.committed !== true
    && snapshot.stageGateOk !== false
    && !isOneToOne;
  const pauseAllowsResetTimer = snapshot.pauseReason === 'lecturer_pause'
    || snapshot.pauseReason === 'help_requested';
  const resetTimerAllowed = inProgress
    && inExamConduct
    && !pending
    && snapshot.committed === true
    && hasQuestion
    && (
      (isOneToOne && !snapshot.isPaused)
      || (!isOneToOne && Boolean(snapshot.isPaused) && pauseAllowsResetTimer)
    );
  const repeatPreviousAllowed = !isOneToOne
    && resetTimerAllowed
    && (snapshot.currentQuestionIndex ?? 0) > 0;
  const startExamAllowed = inProgress
    && inExamConduct
    && !pending
    && isOneToOne
    && snapshot.committed !== true
    && !hasQuestion;

  return {
    dropIn: inProgress && !shouldRemoveInsteadOfDropIn(snapshot),
    next: inProgress && inExamConduct && needsNext,
    takeOver: inProgress && shouldOfferTakeOver(snapshot),
    advanceQuestion: inProgress && inExamConduct && !pending && hasQuestion,
    endExam: inProgress && inExamConduct && !pending && hasQuestion,
    resetExam: resetAllowed,
    resetTimer: resetTimerAllowed,
    repeatPrevious: repeatPreviousAllowed,
    cancelBooking: false,
    abortExam: inProgress && !pending,
    startExam: startExamAllowed,
  };
}

export function resolveOralExamCtaGate(
  snapshot: OralExamJourneySnapshot,
): StudentCtaGate | LecturerCtaGate {
  return snapshot.role === 'student'
    ? resolveStudentCtaGate(snapshot)
    : resolveLecturerCtaGate(snapshot);
}
